import { useEffect, type PropsWithChildren } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { clearStoredReferrerId, getStoredReferrerId } from '../lib/referral';
import { isTmaEnv } from '../lib/tma';
import { Spinner } from './ui';

/**
 * Загружает профиль один раз при старте, кладёт в кеш ['profile'].
 * Дочерние страницы читают тот же useQuery(['profile']) — без рефетча.
 */
export function RequireAuth({ children }: PropsWithChildren) {
  const inTma = isTmaEnv();
  const { data, isLoading, error } = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.getProfile(),
    enabled: inTma,
    staleTime: Infinity,
  });

  // Реферал: новый юзер (до онбординга) пришёл по ссылке — привязываем.
  // Сервер идемпотентен, повторные вызовы безопасны.
  const onboardedAt = data?.user.onboardedAt ?? null;
  useEffect(() => {
    if (!data) return;
    const referrerId = getStoredReferrerId();
    if (referrerId === null) return;
    if (onboardedAt !== null) {
      clearStoredReferrerId();
      return;
    }
    api
      .claimReferral({ referrerId })
      .then(() => clearStoredReferrerId())
      .catch(() => {
        // не критично — попробуем при следующем запуске
      });
  }, [data, onboardedAt]);

  if (!inTma) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center safe-top safe-bottom">
        <div className="max-w-sm">
          <h1 className="text-xl font-semibold mb-2">Открой в Telegram</h1>
          <p className="text-sm text-tg-hint">
            Этот интерфейс предназначен для Telegram Mini App. Открой бота и нажми кнопку «Открыть
            приложение».
          </p>
        </div>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3">
        <Spinner />
        <span className="text-sm text-tg-hint">Загружаем профиль</span>
      </div>
    );
  }

  if (error) {
    const msg = error instanceof ApiError ? `${error.code}: ${error.message}` : String(error);
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center safe-top safe-bottom">
        <div className="max-w-sm">
          <div className="w-14 h-14 rounded-2xl bg-destructive-subtle text-destructive flex items-center justify-center mx-auto mb-3">
            <AlertCircle size={24} />
          </div>
          <h1 className="text-lg font-semibold mb-2">Ошибка авторизации</h1>
          <p className="text-sm text-tg-hint break-words">{msg}</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
